import { memo } from "react";
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { allDisplayedDataBoundsAtom, mapDataListAtom } from "~/components/Map/stores/mapStore";
import { LngLatBounds } from 'maplibre-gl';
import { remote_updateProject } from "~/common/typescript/apis/ProjectAPIs";
import { message, Modal } from "antd";
import {
  showDataTableAtom,
  showCommentPanelAtom,
  showLegendPanelAtom,
  showGeocollectPanelAtom,
  showForestWatchPanelAtom,
  showUploadTaskPanelAtom,
  showMinimapAtom,
  showNavigatorPanelAtom,
} from "~/components/Map/stores/mapStore";
import {
  PiCheckBold,
  PiListBulletsBold,
  PiTableBold,
  PiChatCircleDotsBold,
  PiClipboardTextBold,
  PiTreeBold,
  PiUploadSimpleBold,
  PiCompassBold,
  PiCornersOutBold,
  PiArrowsInBold,
  PiFloppyDiskBold,
  PiSlidersHorizontalBold,
  PiKeyboardBold,
} from "react-icons/pi";
import MapUtils from "~/components/Map/utils/MapUtils";
import { parseBoundsTuple } from "~/components/Map/utils/keyboardShortcutUtils";
import { FaXmark } from "react-icons/fa6";
import { showMapViewAtom } from "~/layouts/components/Header/components/MapView/store";
import { Rnd } from 'react-rnd';

const MapViewSidebarPanel = () => {
  const [showPanel, setShowPanel] = useAtom(showMapViewAtom);
  const currentProject = useAtomValue(currentProjectAtom);
  const setCurrentProject = useSetAtom(currentProjectAtom);
  const allDisplayedDataBounds = useAtomValue(allDisplayedDataBoundsAtom);
  const mapDataList = useAtomValue(mapDataListAtom);

  const [showDataTablePanel, setShowDataTablePanel] = useAtom(showDataTableAtom);
  const [showCommentPanel, setShowCommentPanel] = useAtom<boolean>(showCommentPanelAtom);
  const [showLegendPanel, setShowLegendPanel] = useAtom<boolean>(showLegendPanelAtom);
  const [showGeocollectPanel, setShowGeocollectPanel] = useAtom<boolean>(showGeocollectPanelAtom);
  const [showForestWatchPanel, setShowForestWatchPanel] = useAtom<boolean>(showForestWatchPanelAtom);
  const [showUploadTaskPanel, setShowUploadTaskPanel] = useAtom<boolean>(showUploadTaskPanelAtom);
  const [showMinimap, setShowMinimap] = useAtom<boolean>(showMinimapAtom);
  const [showNavigatorPanel, setShowNavigatorPanel] = useAtom<boolean>(showNavigatorPanelAtom);

  return (
    <Rnd
      default={{
        x: Math.max(10, window.innerWidth - 340),
        y: 60,
        width: 320,
        height: 550,
      }}
      minWidth={280}
      minHeight={320}
      dragHandleClassName="draggable-header"
      cancel=".no-drag, .no-drag *, button, input, textarea, select, a"
      bounds="window"
      enableResizing={{
        bottom: true,
        bottomRight: true,
        right: true,
        top: false,
        topLeft: false,
        topRight: false,
        left: false,
        bottomLeft: false,
      }}
      style={{ zIndex: 1000 }}
    >
      <UI_Wrapper data-testid="map-view-sidebar-panel">
        {/* Header */}
        <UI_Header className="draggable-header" style={{ cursor: 'move', userSelect: 'none' }}>
          <UI_HeaderLeft className="no-drag" style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: 6 }}>
            <PiSlidersHorizontalBold style={{ fontSize: 16, color: '#2563eb' }} />
            <span>Hiển thị & Tính năng</span>
          </UI_HeaderLeft>
          <UI_HeaderRight className="no-drag" style={{ cursor: 'default' }}>
            <UI_HeaderRight_item
              data-testid="map-view-sidebar-close"
              onClick={() => {
                setShowPanel(false);
              }}
              title="Đóng"
            >
              <FaXmark />
            </UI_HeaderRight_item>
          </UI_HeaderRight>
        </UI_Header>
        {/* End Header */}

        {/* Content */}
        <UI_Content>
          <UI_SectionLabel>Lớp hiển thị & Bảng dữ liệu</UI_SectionLabel>
          <UI_List>
            {/* Chú giải */}
            <UI_Item
              data-testid="mapview-item-legend"
              $active={showLegendPanel}
              onClick={() => {
                setShowLegendPanel(!showLegendPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiListBulletsBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Chú giải</UI_Item_Text>
              <UI_Item_Value>Alt + 1</UI_Item_Value>
              <UI_Item_Status>
                {showLegendPanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Chú giải */}

            {/* Bảng dữ liệu */}
            <UI_Item
              data-testid="mapview-item-datatable"
              $active={showDataTablePanel}
              onClick={() => {
                setShowDataTablePanel(!showDataTablePanel);
                MapUtils.triggerResizeEvent();
              }}
            >
              <UI_Item_FeatureIcon>
                <PiTableBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Bảng dữ liệu</UI_Item_Text>
              <UI_Item_Value>Alt + 2</UI_Item_Value>
              <UI_Item_Status>
                {showDataTablePanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Bảng dữ liệu */}

            {/* Bảng ghi chú */}
            <UI_Item
              data-testid="mapview-item-comment"
              $active={showCommentPanel}
              onClick={() => {
                setShowCommentPanel(!showCommentPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiChatCircleDotsBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Bảng ghi chú</UI_Item_Text>
              <UI_Item_Value>Alt + 3</UI_Item_Value>
              <UI_Item_Status>
                {showCommentPanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Bảng ghi chú */}

            {/* Geocollect panel */}
            <UI_Item
              data-testid="mapview-item-geocollect"
              $active={showGeocollectPanel}
              onClick={() => {
                setShowGeocollectPanel(!showGeocollectPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiClipboardTextBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Dữ liệu thu thập (Geocollect)</UI_Item_Text>
              <UI_Item_Value>Alt + 4</UI_Item_Value>
              <UI_Item_Status>
                {showGeocollectPanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Geocollect panel */}

            {/* Satellite panel */}
            <UI_Item
              data-testid="mapview-item-forestwatch"
              $active={showForestWatchPanel}
              onClick={() => {
                setShowForestWatchPanel(!showForestWatchPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiTreeBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Ảnh vệ tinh và cháy rừng</UI_Item_Text>
              <UI_Item_Value>Alt + 5</UI_Item_Value>
              <UI_Item_Status>
                {showForestWatchPanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Satellite panel */}

            {/* Danh sách tải lên */}
            <UI_Item
              data-testid="mapview-item-uploadtask"
              $active={showUploadTaskPanel}
              onClick={() => {
                setShowUploadTaskPanel(!showUploadTaskPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiUploadSimpleBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Tiến trình tải lên</UI_Item_Text>
              <UI_Item_Value>Alt + 6</UI_Item_Value>
              <UI_Item_Status>
                {showUploadTaskPanel && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Danh sách tải lên */}

            {/* Bản đồ con */}
            <UI_Item
              data-testid="mapview-item-minimap"
              $active={showMinimap}
              onClick={() => {
                setShowMinimap(!showMinimap);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiCompassBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Bản đồ con</UI_Item_Text>
              <UI_Item_Value>Alt + 7</UI_Item_Value>
              <UI_Item_Status>
                {showMinimap && <PiCheckBold style={{ color: '#2563eb' }} />}
              </UI_Item_Status>
            </UI_Item>
            {/* End Bản đồ con */}
          </UI_List>

          <UI_Item_Divider />

          <UI_SectionLabel>Công cụ camera & góc nhìn</UI_SectionLabel>
          <UI_List>
            {/* Phóng tới dữ liệu */}
            <UI_Item
              data-testid="mapview-item-zoom-data"
              onClick={() => {
                const tuple = parseBoundsTuple(allDisplayedDataBounds);
                if (tuple) {
                  MapUtils.zoomToDefaultView(new LngLatBounds(tuple[0], tuple[1]));
                } else {
                  if (mapDataList.length > 0) {
                    message.info(`Có ${mapDataList.length} dữ liệu bản đồ nhưng chưa có thông tin vùng hiển thị. Vui lòng kiểm tra dữ liệu bản đồ.`);
                  } else {
                    message.info("Không có dữ liệu bản đồ để phóng tới.");
                  }
                }
              }}
            >
              <UI_Item_FeatureIcon>
                <PiCornersOutBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Phóng tới dữ liệu</UI_Item_Text>
              <UI_Item_Value>Ctrl + F</UI_Item_Value>
            </UI_Item>
            {/* End phóng tới dữ liệu */}

            {/* Thu phóng mặc định */}
            <UI_Item
              data-testid="mapview-item-default-zoom"
              onClick={() => {
                const tuple = parseBoundsTuple(currentProject?.boundingBox);
                const bounds = tuple ? new LngLatBounds(tuple[0], tuple[1]) : undefined;
                MapUtils.zoomToDefaultView(bounds);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiArrowsInBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Thu phóng mặc định</UI_Item_Text>
              <UI_Item_Value>Ctrl + 0</UI_Item_Value>
            </UI_Item>
            {/* End Thu phóng mặc định */}

            {/* Lưu thu phóng mặc định */}
            <UI_Item
              data-testid="mapview-item-save-zoom"
              onClick={() => {
                Modal.confirm({
                  title: 'Lưu thu phóng mặc định',
                  content: (
                    <div>
                      <p>Bạn có chắc chắn muốn lưu khung nhìn hiện tại làm thu phóng mặc định cho dự án này?</p>
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '12px' }}>
                        <strong>Lưu ý:</strong> Hãy điều chỉnh camera để hiển thị khu vực dự án mong muốn trước khi lưu.
                      </p>
                    </div>
                  ),
                  okText: 'Lưu',
                  cancelText: 'Hủy',
                  onOk: async () => {
                    if (window.map && currentProject) {
                      const bounds = window.map.getBounds();
                      const newBoundingBox = JSON.stringify([
                        [bounds.getWest(), bounds.getSouth()],
                        [bounds.getEast(), bounds.getNorth()],
                      ]);

                      const updatedProject = await remote_updateProject({
                        boundingBox: newBoundingBox,
                      }, currentProject.id!);

                      if (updatedProject.data) {
                        setCurrentProject(updatedProject.data);
                        message.success("Đã lưu thu phóng mặc định!");
                      } else {
                        message.error("Lỗi khi lưu thu phóng mặc định.");
                      }
                    }
                  },
                });
              }}
            >
              <UI_Item_FeatureIcon>
                <PiFloppyDiskBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Lưu thu phóng mặc định</UI_Item_Text>
              <UI_Item_Value>Ctrl + S</UI_Item_Value>
            </UI_Item>
            {/* End Lưu thu phóng mặc định */}

            {/* Hướng dẫn & Bảng phím tắt */}
            <UI_Item
              data-testid="mapview-item-shortcuts-guide"
              $active={showNavigatorPanel}
              onClick={() => {
                setShowNavigatorPanel(!showNavigatorPanel);
              }}
            >
              <UI_Item_FeatureIcon>
                <PiKeyboardBold />
              </UI_Item_FeatureIcon>
              <UI_Item_Text>Hướng dẫn & Phím tắt</UI_Item_Text>
              <UI_Item_Value>? / Ctrl + K</UI_Item_Value>
            </UI_Item>
            {/* End Hướng dẫn & Bảng phím tắt */}
          </UI_List>
        </UI_Content>
      </UI_Wrapper>
    </Rnd>
  );
};

export default memo(MapViewSidebarPanel);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
`;

const UI_Content = styled.div`
  padding: 8px;
  flex: 1;
  overflow-y: auto;
`;

const UI_SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  padding: 6px 8px 4px 8px;
`;

const UI_List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UI_Item = styled.div<{ $active?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.08)' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.14)' : 'rgba(240, 240, 240, 0.8)')};
  }
`;

const UI_Item_FeatureIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #475569;
  flex-shrink: 0;
`;

const UI_Item_Text = styled.div`
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  padding: 0 10px;
  line-height: 1.4;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UI_Item_Status = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const UI_Item_Value = styled.div`
  min-width: 50px;
  font-size: 11px;
  text-align: right;
  color: #94a3b8;
  font-family: monospace;
  flex-shrink: 0;
`;

const UI_Item_Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f1f5f9;
  margin: 6px 0;
`;
